import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ClientDashboardTabs from '../../components/navigation/ClientDashboardTabs';
import LogoUploadSection from './components/LogoUploadSection';
import BrandColorPicker from './components/BrandColorPicker';
import ContactInformationForm from './components/ContactInformationForm';
import CTAConfigurationForm from './components/CTAConfigurationForm';
import BrandPreviewPanel from './components/BrandPreviewPanel';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { companyService } from '../../services/companyService';

const CompanyProfileManagement = () => {
  const navigate = useNavigate();
  const { companyIdentifier } = useParams();
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [errors, setErrors] = useState({});
  const [companyId, setCompanyId] = useState(null);
  const [companyNotFound, setCompanyNotFound] = useState(false);

  const [profileData, setProfileData] = useState({
    companyName: '',
    businessDescription: '',
    logoFile: null,
    logoPreview: null,
    primaryColor: '#2563EB',
    secondaryColor: '#64748B',
    phone: '',
    email: '',
    website: '',
    address: '',
    ctaButtonText: 'Get Free Quote',
    ctaUrl: '',
  });

  useEffect(() => {
    const loadCompanyData = async () => {
      if (!companyIdentifier) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setCompanyNotFound(false);

        // Extract slug from compound identifier (format: slug-uuid)
        const extractSlugFromIdentifier = (identifier) => {
          const parts = identifier?.split('-');
          let uuidStartIndex = -1;
          
          for (let i = parts?.length - 1; i >= 0; i--) {
            if (parts?.[i]?.length === 12 && /^[a-f0-9]+$/i?.test(parts?.[i])) {
              if (i >= 4 && 
                  parts?.[i-1]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i-1]) &&
                  parts?.[i-2]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i-2]) &&
                  parts?.[i-3]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i-3]) &&
                  parts?.[i-4]?.length === 8 && /^[a-f0-9]+$/i?.test(parts?.[i-4])) {
                uuidStartIndex = i - 4;
                break;
              }
            }
          }
          
          if (uuidStartIndex > 0) {
            return parts?.slice(0, uuidStartIndex)?.join('-');
          }
          return identifier;
        };
        
        const slug = extractSlugFromIdentifier(companyIdentifier);
        const normalizedSlug = decodeURIComponent(slug || '')?.toLowerCase()?.trim();
        
        console.log('Loading company profile for slug:', normalizedSlug);
        
        const companyData = await companyService?.getBySlug(normalizedSlug);
        
        if (companyData) {
          setCompanyId(companyData?.id);
          setProfileData({
            companyName: companyData?.name || '',
            businessDescription: companyData?.description || '',
            logoFile: null,
            logoPreview: companyData?.logo || null,
            primaryColor: companyData?.primaryColor || '#2563EB',
            secondaryColor: companyData?.secondaryColor || '#64748B',
            phone: companyData?.phone || '',
            email: companyData?.email || '',
            website: companyData?.website || '',
            address: companyData?.address || '',
            ctaButtonText: companyData?.ctaText || 'Get Free Quote',
            ctaUrl: companyData?.ctaUrl || '',
          });
        } else {
          setCompanyNotFound(true);
          console.warn('Company not found for slug:', normalizedSlug);
        }
      } catch (error) {
        console.error('Error loading company data:', error);
        setCompanyNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    
    loadCompanyData();
  }, [companyIdentifier]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!profileData?.companyName || profileData?.companyName?.trim()?.length < 2) {
      newErrors.companyName = 'Company name must be at least 2 characters';
    }

    if (!profileData?.businessDescription || profileData?.businessDescription?.trim()?.length < 20) {
      newErrors.businessDescription = 'Description must be at least 20 characters';
    }

    if (!profileData?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(profileData?.email)) {
      newErrors.email = 'Valid email address is required';
    }

    if (profileData?.website && !/^https?:\/\/.+/?.test(profileData?.website)) {
      newErrors.website = 'Website must be a valid URL starting with http:// or https://';
    }

    if (!profileData?.ctaButtonText || profileData?.ctaButtonText?.trim()?.length === 0) {
      newErrors.ctaButtonText = 'CTA button text is required';
    }

    if (!profileData?.ctaUrl || !/^https?:\/\/.+/?.test(profileData?.ctaUrl)) {
      newErrors.ctaUrl = 'Valid CTA URL is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleLogoChange = (file, previewUrl) => {
    setProfileData({
      ...profileData,
      logoFile: file,
      logoPreview: previewUrl
    });
  };

  const handleLogoRemove = () => {
    setProfileData({
      ...profileData,
      logoFile: null,
      logoPreview: null
    });
  };

  const handleContactInfoChange = (newContactData) => {
    setProfileData({
      ...profileData,
      ...newContactData
    });
  };

  const handleCTAChange = (newCTAData) => {
    setProfileData({
      ...profileData,
      ...newCTAData
    });
  };

  const handleSave = async () => {
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!companyId) {
      setErrors({ general: 'Company ID not found. Please reload the page.' });
      return;
    }

    setIsSaving(true);

    try {
      const updateData = {
        name: profileData?.companyName,
        description: profileData?.businessDescription,
        logo: profileData?.logoPreview,
        primaryColor: profileData?.primaryColor,
        secondaryColor: profileData?.secondaryColor,
        email: profileData?.email,
        phone: profileData?.phone,
        website: profileData?.website,
        address: profileData?.address,
        ctaText: profileData?.ctaButtonText,
        ctaUrl: profileData?.ctaUrl,
      };

      await companyService?.update(companyId, updateData);
      
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error saving company profile:', error);
      setErrors({ 
        general: 'Failed to save profile. Please try again.' 
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/client-dashboard-home');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <ClientDashboardTabs />
        <div className="main-content-with-tabs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                <p className="text-text-secondary">Loading company profile...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (companyNotFound) {
    return (
      <div className="min-h-screen bg-background">
        <ClientDashboardTabs />
        <div className="main-content-with-tabs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-text-primary mb-2">Company Not Found</h2>
              <p className="text-text-secondary mb-6">
                The company you're looking for doesn't exist or has been deactivated.
              </p>
              <Button
                variant="default"
                onClick={() => navigate('/admin-dashboard')}
                iconName="ArrowLeft"
                iconPosition="left"
                iconSize={16}
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ClientDashboardTabs />
      <div className="main-content-with-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">Company Profile</h1>
                <p className="text-text-secondary">
                  Configure your brand identity and customer-facing visualizer settings
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleCancel}
                iconName="ArrowLeft"
                iconPosition="left"
                iconSize={16}>

                Back to Dashboard
              </Button>
            </div>
          </div>

          {showSuccessMessage && (
          <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="CheckCircle" size={20} className="text-success" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-success">Profile Updated Successfully</p>
                  <p className="text-xs text-success/80 mt-1">
                    Your changes are now live across all visualizer instances
                  </p>
                </div>
              </div>
            </div>
          )}

          {errors?.general && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-error">{errors?.general}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-text-primary mb-1">Basic Information</h3>
                  <p className="text-sm text-text-secondary">
                    Company details displayed throughout the visualizer experience
                  </p>
                </div>

                <div className="space-y-6">
                  <Input
                    label="Company Name"
                    type="text"
                    placeholder="Enter company name"
                    value={profileData?.companyName}
                    onChange={(e) => setProfileData({ ...profileData, companyName: e?.target?.value })}
                    error={errors?.companyName}
                    required
                    description="Your business name as it appears to customers" />


                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Business Description <span className="text-error">*</span>
                    </label>
                    <textarea
                      value={profileData?.businessDescription}
                      onChange={(e) => setProfileData({ ...profileData, businessDescription: e?.target?.value })}
                      placeholder="Describe your business, services, and expertise..."
                      rows={5}
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none" />

                    {errors?.businessDescription &&
                    <p className="mt-1 text-sm text-error">{errors?.businessDescription}</p>
                    }
                    <p className="mt-1 text-xs text-text-secondary">
                      Minimum 20 characters. This appears on your visualizer landing page.
                    </p>
                  </div>
                </div>
              </div>

              <LogoUploadSection
                currentLogo={profileData?.logoPreview}
                onLogoChange={handleLogoChange}
                onLogoRemove={handleLogoRemove} />


              <div className="bg-card border border-border rounded-lg p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-text-primary mb-1">Brand Colors</h3>
                  <p className="text-sm text-text-secondary">
                    Customize colors to match your brand identity
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <BrandColorPicker
                    label="Primary Color"
                    value={profileData?.primaryColor}
                    onChange={(color) => setProfileData({ ...profileData, primaryColor: color })}
                    description="Main brand color used for headers and primary actions" />


                  <BrandColorPicker
                    label="Secondary Color"
                    value={profileData?.secondaryColor}
                    onChange={(color) => setProfileData({ ...profileData, secondaryColor: color })}
                    description="Accent color for secondary elements and highlights" />

                </div>
              </div>

              <ContactInformationForm
                formData={profileData}
                onChange={handleContactInfoChange}
                errors={errors} />


              <CTAConfigurationForm
                formData={profileData}
                onChange={handleCTAChange}
                errors={errors} />


              <div className="flex items-center justify-end gap-4 pt-6 border-t border-border">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSaving}>

                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleSave}
                  loading={isSaving}
                  iconName="Save"
                  iconPosition="left"
                  iconSize={16}>

                  {isSaving ? 'Saving Changes...' : 'Save Profile'}
                </Button>
              </div>
            </div>

            <div className="lg:col-span-1">
              <BrandPreviewPanel profileData={profileData} />
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default CompanyProfileManagement;